import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { useAuth } from '../../../context/AuthContext';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const HIGH5_BRAND_ID = 'b20e13f1-2e3f-4c24-83ce-f2b812bfc3e8';

/**
 * Kampanya × dönem gösterim payı tablosu — tek başına sayfa.
 * Route: /ads-platforms/google-ads/impression-share
 *
 * /ads-platforms/google-ads/visibility sayfasındaki "Kampanya × dönem"
 * sekmesinin yalnız tablo hâli. Veri aynı uçtan gelir
 * (/api/ads-visibility/matrix, backend'de 10 dk önbellek).
 *
 * 🔑 Marka bağlamı useAuth().selectedBrand'den okunur — useBrand() bozuk.
 */

const fmtInt = (n) => Number(Math.round(n || 0)).toLocaleString('tr-TR');
const fmtMoney = (n) => `₺${Number(Math.round(n || 0)).toLocaleString('tr-TR')}`;
const fmtPct = (n, d = 1) => `%${(Number(n || 0) * 100).toFixed(d).replace('.', ',')}`;

const TR_AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const monthLabel = (p) => `${TR_AY[Number(String(p).slice(5, 7)) - 1]} ${String(p).slice(0, 4)}`;
const weekLabel = (p) => `${String(p).slice(8, 10)}.${String(p).slice(5, 7)}`;
const TR_GUN = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const dayLabel = (p) => `${weekLabel(p)} ${TR_GUN[new Date(`${p}T12:00:00Z`).getUTCDay()]}`;
const GRAINS = [['monthly', 'Aylık'], ['weekly', 'Haftalık'], ['daily', 'Günlük']];
const GRAIN_FILE = { monthly: 'aylik', weekly: 'haftalik', daily: 'gunluk' };

const MATRIX_METRICS = [
  { key: 'is', label: 'Gösterim payı', kind: 'share' },
  { key: 'budgetLost', label: 'Bütçe kaybı', kind: 'loss' },
  { key: 'rankLost', label: 'Sıralama kaybı', kind: 'loss' },
  // absTop = sayfanın en üst reklam yerinde çıkma oranı; top = organik sonuçların
  // üstündeki reklam bloğunda (herhangi bir sırada) çıkma oranı. İkisi de gösterim
  // payı DEĞİL: müşteri metninde "gösterim payı" yalnız `is` için kullanılır.
  { key: 'absTop', label: 'En üstte çıkma oranı', kind: 'share' },
  { key: 'top', label: 'Üst blokta çıkma oranı', kind: 'share' },
  { key: 'impressions', label: 'Gösterim', kind: 'count' },
];
const shareTone = (v) => (v >= 0.8 ? 'bg-emerald-50 text-emerald-800'
  : v >= 0.6 ? 'bg-lime-50 text-lime-800'
  : v >= 0.4 ? 'bg-amber-50 text-amber-800'
  : 'bg-rose-50 text-rose-700');
const lossTone = (v) => (v >= 0.4 ? 'bg-rose-50 text-rose-700'
  : v >= 0.25 ? 'bg-amber-50 text-amber-800'
  : v >= 0.1 ? 'bg-gray-50 text-gray-600'
  : 'bg-white text-gray-400');

function MatrixCell({ cell, metric }) {
  // Boş hücre: kampanya o dönemde yayında değil. %0 DEĞİL, veri yok.
  if (!cell) return <td className="px-2 py-1.5 text-center text-gray-300">·</td>;
  const kind = MATRIX_METRICS.find((m) => m.key === metric)?.kind || 'share';
  const v = cell[metric] || 0;
  const cls = kind === 'count' ? 'text-gray-700' : kind === 'loss' ? lossTone(v) : shareTone(v);
  const tip = [
    `Gösterim payı ${fmtPct(cell.is)}`,
    `bütçe kaybı ${fmtPct(cell.budgetLost)}`,
    `sıralama kaybı ${fmtPct(cell.rankLost)}`,
    `en üstte ${fmtPct(cell.absTop)}`,
    `üst blokta ${fmtPct(cell.top)}`,
    `gösterim ${fmtInt(cell.impressions)} / havuz ${fmtInt(cell.pool)}`,
    `maliyet ${fmtMoney(cell.cost)}`,
  ].join(' · ');
  return (
    <td className={`px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${cls}`} title={tip}>
      {kind === 'count' ? fmtInt(v) : fmtPct(v)}
      {((metric === 'is' && cell.bucketed) || (metric === 'absTop' && cell.absTopBucketed)) && (
        <span className="text-[10px] text-gray-400 align-super" title="Google bu değeri kova olarak veriyor: '%10'dan az' / '%90'dan fazla'.">*</span>
      )}
    </td>
  );
}

/**
 * Canlı çekim 10-25 sn sürüyor. Boş kutu yerine: dönen simge, geçen saniye,
 * ~20 sn'ye göre dolan (%95'te duran) çubuk ve arkada soluk tablo iskeleti.
 */
function LoadingState() {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSec((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const pct = Math.min(95, Math.round((1 - Math.exp(-sec / 9)) * 100));
  const step = sec < 4 ? 'Google Ads\'e bağlanılıyor'
    : sec < 12 ? 'Arama kampanyaları ve dönem verileri çekiliyor'
    : sec < 25 ? 'Gösterim payları hesaplanıyor'
    : 'Neredeyse bitti — Google bugün biraz yavaş';
  return (
    <div className="relative rounded-xl border border-gray-200 overflow-hidden">
      <div className="animate-pulse">
        <div className="h-10 bg-gray-100 border-b border-gray-200" />
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3 h-11 border-b border-gray-100">
            <div className="h-3 w-48 rounded bg-gray-200" />
            {Array.from({ length: 9 }).map((__, j) => (
              <div key={j} className="h-3 flex-1 rounded bg-gray-100" />
            ))}
          </div>
        ))}
      </div>
      <div className="absolute inset-0 grid place-items-center bg-white/60">
        <div className="w-[340px] rounded-2xl border border-gray-200 bg-white shadow-sm px-5 py-4 text-center">
          <Loader2 className="w-7 h-7 mx-auto text-blue-600 animate-spin" />
          <div className="mt-2 text-sm font-semibold text-gray-800">{step}</div>
          <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 text-[11px] text-gray-400 tabular-nums">
            {sec} sn · genelde 10-25 sn sürer
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Marka kampanyaları × hafta — /high5/acik-artirma verisinin haftalık hâli.
 * Kampanya satırında BİZİM gösterim payımız; satır açılınca rakiplerin
 * haftalık GÜN ORANI (rakibin göründüğü gün ÷ bizim açık artırmadaki günümüz).
 *
 * 🔴🔴 Rakip gösterim payı API'den gelmez (metrics.auction_insight_* jetona
 *    kapalı, 18 Eyl 2026'da yeniden denendi: METRIC_ACCESS_DENIED). Rakip
 *    hücreleri gösterim payı DEĞİL, varlık oranıdır; başlıkta öyle yazar.
 */
const WEEK_OPTS = [8, 12, 16];
const presenceBg = (v) => `rgba(15, 118, 110, ${(0.06 + Math.min(1, v) * 0.6).toFixed(3)})`;

function BrandCampaignWeekly() {
  const [weeksN, setWeeksN] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await fetch(`${API_BASE}/api/high5-auction-insights?weeks=${weeksN}`, { credentials: 'include' });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
        if (alive) setData(j);
      } catch (e) {
        if (alive) setError(e.message || 'Marka kampanyaları yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [weeksN]);

  const weeks = data?.weeks || [];
  const lastW = weeks[weeks.length - 1];
  const prevW = weeks[weeks.length - 2];

  // Rakip sırası: son haftada en çok sahnede olan üstte; eşitlikte son
  // haftaya göre en çok artan, sonra toplam gün.
  const sortedCompetitors = (c) => [...c.competitors].sort((a, b) => {
    const la = a.weekly?.[lastW]?.presence || 0;
    const lb = b.weekly?.[lastW]?.presence || 0;
    if (lb !== la) return lb - la;
    const da = la - (a.weekly?.[prevW]?.presence || 0);
    const db = lb - (b.weekly?.[prevW]?.presence || 0);
    return db - da || b.days - a.days;
  });

  return (
    <Card className="rounded-2xl border border-gray-200 overflow-hidden mt-4">
      <div className="p-4">
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div className="mr-auto">
            <h2 className="font-semibold text-gray-900">Marka kampanyaları — haftalık</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Satırda bizim gösterim payımız. Satıra tıkla: o kampanyadaki rakiplerin hafta hafta ne kadar sahnede olduğu açılır.
            </p>
          </div>
          {WEEK_OPTS.map((n) => (
            <button
              key={n}
              onClick={() => setWeeksN(n)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${weeksN === n ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >{n} hafta</button>
          ))}
          {data && <span className="text-xs text-gray-400 ml-1">{data.start} → {data.end}</span>}
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-3">{error}</div>
        )}
        {loading && <LoadingState />}

        {!loading && data && (
          <div className="rounded-xl border border-gray-200 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2.5 text-left sticky left-0 bg-gray-50 z-10 min-w-[240px]">Kampanya / rakip</th>
                  {weeks.map((w) => (
                    <th key={w} className="px-2 py-2.5 text-center whitespace-nowrap">{weekLabel(w)}</th>
                  ))}
                  <th className="px-3 py-2.5 text-center border-l border-gray-200 whitespace-nowrap">Son hf. farkı</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.campaigns.map((c) => {
                  const isOpen = !!open[c.campaignId];
                  const lastIs = c.weekly?.[lastW];
                  const prevIs = c.weekly?.[prevW];
                  const dIs = lastIs && prevIs ? lastIs.is - prevIs.is : null;
                  return (
                    <React.Fragment key={c.campaignId}>
                      <tr
                        className="hover:bg-gray-50/70 cursor-pointer"
                        onClick={() => setOpen((o) => ({ ...o, [c.campaignId]: !o[c.campaignId] }))}
                      >
                        <td className="px-3 py-1.5 sticky left-0 bg-white z-10">
                          <div className="flex items-center gap-1.5">
                            {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <span className="font-semibold text-gray-900">{c.brand}</span>
                            {c.status && c.status !== 'ENABLED' && <span className="text-[10px] text-amber-600">{c.status}</span>}
                          </div>
                          <div className="text-[10px] text-gray-400 pl-5">{c.competitorCount} rakip · {fmtMoney(c.metrics.cost)}</div>
                        </td>
                        {weeks.map((w) => {
                          const cell = c.weekly?.[w];
                          if (!cell || !cell.pool) return <td key={w} className="px-2 py-1.5 text-center text-gray-300">·</td>;
                          return (
                            <td
                              key={w}
                              className={`px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${shareTone(cell.is)}`}
                              title={`Gösterim payı ${fmtPct(cell.is)} · gösterim ${fmtInt(cell.impressions)} / havuz ${fmtInt(cell.pool)} · ${cell.activeDays} gün · ${fmtMoney(cell.cost)}`}
                            >
                              {fmtPct(cell.is)}
                              {cell.bucketed && <span className="text-[10px] text-gray-400 align-super">*</span>}
                            </td>
                          );
                        })}
                        <td className={`px-3 py-1.5 text-center tabular-nums font-semibold border-l border-gray-200 ${dIs == null ? 'text-gray-300' : dIs >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                          {dIs == null ? '—' : `${dIs >= 0 ? '+' : '−'}${Math.abs(dIs * 100).toFixed(1).replace('.', ',')} puan`}
                        </td>
                      </tr>

                      {isOpen && (
                        <>
                          <tr className="bg-gray-50/60">
                            <td colSpan={weeks.length + 2} className="px-3 py-1.5 text-[11px] text-gray-500">
                              Rakip hücresi = o hafta göründüğü gün ÷ bizim o hafta açık artırmada olduğumuz gün.
                              Gösterim payı değil; Google rakip yüzdelerini API'den vermiyor.
                              <span className="ml-1 inline-block w-2.5 h-2.5 rounded-sm ring-2 ring-rose-500 align-middle" /> = o hafta ilk kez göründü.
                            </td>
                          </tr>
                          {sortedCompetitors(c).map((k) => {
                            const lp = k.weekly?.[lastW]?.presence || 0;
                            const pp = k.weekly?.[prevW]?.presence || 0;
                            const d = lp - pp;
                            return (
                              <tr key={k.domain} className="bg-gray-50/30">
                                <td className="px-3 py-1 sticky left-0 bg-gray-50 z-10 pl-8 text-gray-700">{k.domain}</td>
                                {weeks.map((w, i) => {
                                  const cell = k.weekly?.[w];
                                  if (!cell) return <td key={w} className="px-2 py-1 text-center text-gray-200">·</td>;
                                  const entered = i > 0 && !k.weekly?.[weeks[i - 1]] && k.firstSeen >= w;
                                  return (
                                    <td key={w} className="px-1 py-1 text-center">
                                      <span
                                        className={`inline-block min-w-[38px] rounded px-1 py-0.5 tabular-nums font-semibold ${cell.presence >= 0.6 ? 'text-white' : 'text-gray-800'} ${entered ? 'ring-2 ring-rose-500' : ''}`}
                                        style={{ background: presenceBg(cell.presence) }}
                                        title={`${k.domain} · ${weekLabel(w)} haftası ${cell.days} gün`}
                                      >
                                        {Math.round(cell.presence * 100)}
                                      </span>
                                    </td>
                                  );
                                })}
                                <td className={`px-3 py-1 text-center tabular-nums border-l border-gray-200 ${Math.abs(d) < 0.01 ? 'text-gray-300' : d > 0 ? 'text-rose-600 font-semibold' : 'text-emerald-700'}`}>
                                  {Math.abs(d) < 0.01 ? '—' : `${d > 0 ? '+' : '−'}${Math.round(Math.abs(d) * 100)}`}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-2">
          Kampanya satırı bizim haftalık gösterim payımız (havuza göre ağırlıklı); <span className="align-super">*</span> Google&apos;ın &quot;%10&apos;dan az&quot; / &quot;%90&apos;dan fazla&quot; kovası.
          Rakip satırları gösterim payı değil gün oranıdır: rakibin o hafta açık artırmada göründüğü gün sayısı ÷ bizim o hafta açık artırmada olduğumuz gün sayısı.
          Süreklilik ölçer, yoğunluk ölçmez. Sütun başlığı haftanın Pazartesi&apos;si; ilk ve son hafta eksik gün içerebilir.
          &quot;Son hf. farkı&quot; son sütunla bir önceki sütun arasındaki fark (rakipte artış kırmızı).
        </p>
      </div>
    </Card>
  );
}

export default function ImpressionShareMatrix() {
  const { selectedBrand } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mGrain, setMGrain] = useState('monthly');
  const [mMetric, setMMetric] = useState('is');

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'X-Brand-ID': selectedBrand?.id || '',
  }), [selectedBrand?.id]);

  useEffect(() => {
    if (!selectedBrand?.id) return;
    let alive = true;
    setMatrix(null);
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await fetch(`${API_BASE}/api/ads-visibility/matrix`, { credentials: 'include', headers });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
        if (alive) setMatrix(j);
      } catch (e) {
        if (alive) setError(e.message || 'Tablo yüklenemedi');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedBrand?.id, headers]);

  const mx = matrix?.[mGrain] || null;
  const mLabel = (p) => (mGrain === 'monthly' ? monthLabel(p) : mGrain === 'daily' ? dayLabel(p) : weekLabel(p));
  const range = mGrain === 'daily' ? (matrix?.dailyRange || matrix?.range) : matrix?.range;

  const downloadCsv = () => {
    if (!mx) return;
    const head = ['Kampanya', 'Durum', ...mx.periods.map(mLabel), 'Toplam'];
    const val = (cell) => (cell == null ? '' : mMetric === 'impressions'
      ? Math.round(cell.impressions)
      : String((cell[mMetric] || 0).toFixed(4)).replace('.', ','));
    const lines = [head, ...mx.campaigns.map((c) => [
      c.name, c.status, ...mx.periods.map((p) => val(c.cells[p])), val(c.total),
    ]), ['TOPLAM', '', ...mx.periods.map((p) => val(mx.periodTotals[p])), val(mx.total)]];
    const csv = '\uFEFF' + lines.map((r) => r.map((x) => `"${String(x ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `gosterim-payi-${GRAIN_FILE[mGrain]}-${mMetric}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 md:p-6">
      <Card className="rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {GRAINS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => setMGrain(k)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${mGrain === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >{l}</button>
            ))}
            <span className="w-px h-6 bg-gray-200 mx-1" />
            {MATRIX_METRICS.map((m) => (
              <button
                key={m.key}
                onClick={() => setMMetric(m.key)}
                className={`px-2.5 py-1.5 rounded-lg text-xs border ${mMetric === m.key ? 'bg-blue-50 text-blue-700 border-blue-200 font-semibold' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
              >{m.label}</button>
            ))}
            {matrix && (
              <>
                <span className="text-xs text-gray-400 ml-1">{range.start} → {range.end}{mGrain === 'daily' && ' · son 30 gün'}</span>
                <button
                  onClick={downloadCsv}
                  className="ml-auto px-3 py-1.5 rounded-lg text-xs border border-gray-200 text-gray-600 hover:bg-gray-50"
                >CSV indir</button>
              </>
            )}
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-3">{error}</div>
          )}

          {loading && <LoadingState />}

          {!loading && mx && !mx.campaigns.length && (
            <div className="rounded-xl border border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
              Bu pencerede gösterim almış arama kampanyası yok.
            </div>
          )}

          {!loading && !!mx?.campaigns.length && (
            <div className="rounded-xl border border-gray-200 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2.5 text-left sticky left-0 bg-gray-50 z-10 min-w-[240px]">Kampanya</th>
                    {mx.periods.map((pr) => (
                      <th key={pr} className="px-2 py-2.5 text-center whitespace-nowrap">{mLabel(pr)}</th>
                    ))}
                    <th className="px-3 py-2.5 text-center border-l border-gray-200">Toplam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {mx.campaigns.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50/70">
                      <td className="px-3 py-1.5 sticky left-0 bg-white z-10">
                        <div className="font-medium text-gray-800">{c.name}</div>
                        <div className="text-[10px] text-gray-400">
                          {c.status !== 'ENABLED' && <span className="mr-1 text-amber-600">{c.status}</span>}
                          {fmtMoney(c.total.cost)} · {fmtInt(c.total.impressions)} gösterim
                        </div>
                      </td>
                      {mx.periods.map((pr) => <MatrixCell key={pr} cell={c.cells[pr]} metric={mMetric} />)}
                      <td className="px-3 py-1.5 text-center tabular-nums font-semibold text-gray-800 border-l border-gray-200">
                        {mMetric === 'impressions' ? fmtInt(c.total.impressions) : fmtPct(c.total[mMetric])}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-gray-700">
                  <tr>
                    <td className="px-3 py-2 sticky left-0 bg-gray-50 z-10">
                      Hesap toplamı <span className="font-normal text-gray-400">· {mx.campaigns.length} kampanya</span>
                    </td>
                    {mx.periods.map((pr) => (
                      <td key={pr} className="px-2 py-2 text-center tabular-nums">
                        {mMetric === 'impressions'
                          ? fmtInt(mx.periodTotals[pr]?.impressions)
                          : fmtPct(mx.periodTotals[pr]?.[mMetric])}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center tabular-nums border-l border-gray-200">
                      {mMetric === 'impressions' ? fmtInt(mx.total.impressions) : fmtPct(mx.total[mMetric])}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <p className="text-[11px] text-gray-400 mt-2">
            Hücre Google'ın o dönem için verdiği ham değer; üstüne gelince gösterim, havuz ve kayıp kırılımı çıkar.
            En üstte çıkma oranı, reklamın sayfadaki ilk reklam yerinde göründüğü gösterimlerin uygun gösterim havuzuna oranıdır; üst blokta çıkma oranı, organik sonuçların üstündeki reklam bloğunda (herhangi bir sırada) göründüğü gösterimlerin oranıdır. İkisi de gösterim payı değildir.
            Boş hücre (·) o dönemde kampanyanın yayında olmadığını söyler, %0 demek değildir.
            <span className="align-super">*</span> işaretli hücrede Google gerçek sayıyı vermiyor, &quot;%10&apos;dan az&quot; / &quot;%90&apos;dan fazla&quot; kovası.
            Satır ve sütun toplamları dönemlerin düz ortalaması değil, uygun gösterim havuzuna göre ağırlıklı.
            Haftalıkta ilk ve son sütun pencerenin kenarına denk geldiği için eksik gün içerir; sütun başlığı haftanın Pazartesi'sidir.
            Günlük görünüm son 30 günü (düne kadar) gösterir; tek günlük pay gürültülüdür, eğilim için haftalığa bakın.
          </p>
        </div>
      </Card>

      {selectedBrand?.id === HIGH5_BRAND_ID && <BrandCampaignWeekly />}
    </div>
  );
}
